(ns app.domain.application.guards.states
  (:require [app.domain.acceptance.model :as acceptance-model]
            [app.domain.application.guards.utils :as guard-utils]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.fee.model :as fee-model]
            [app.domain.form.model :as form-model]
            [app.domain.util :as du]
            [core.db.entity.util :as eu]
            [potpuri.core :as p]
            [tilakone.core :as tk]))

(def child-entity-in-state?
  "The guard expects two arguments, the first being the key under which the child entity id is in the signal payload
   and the other a vector of allowed states for this guard to succeed. e.g.:
   `[:process.guard/child-entity-in-state? :approval-id #{:approval.state/draft}]`"
  {:name    :process.guard/child-entity-in-state?
   :type    :guard
   :handler (fn [fsm]
              (p/if-all-let [[_ entity-k states] (-> fsm ::tk/guard)
                             target-entity (guard-utils/find-child-entity fsm entity-k)]
                (or (-> fsm (guard-utils/get-param entity-k) nil?) ;; allow check with partial signal (i.e. missing id)
                    (contains? (set states) (:domain/state target-entity)))))})

(defn some-child-entity-in-state-handler [fsm]
  (p/if-all-let [[_ entity-k entity-type states] (-> fsm ::tk/guard)
                 target-entity  (guard-utils/find-child-entity fsm entity-k)
                 child-entities (->> target-entity
                                     :entity/_parent
                                     (filter (eu/is-type-fn entity-type)))]
    (some (apply du/in-state-fn states) child-entities)))

(def some-child-entity-in-state?
  "The guard expects three arguments, the first being the key under which the child entity id is in the signal payload
   second is the entity-type that are filtered from the target's children defined by the first argument
   and the third is a vector of allowed states that has to be found for this guard to succeed. e.g.:
   `[:process.guard/some-child-entity-in-state? :topic-id :entity.type/checklist-comment [:checklist.comment.state/draft]]`"
  {:name    :process.guard/some-child-entity-in-state?
   :type    :guard
   :handler some-child-entity-in-state-handler})

(def no-child-entity-in-state?
  "Opposite of some-child-entity-in-state, aka no child-of-specific-child should be in a given state"
  {:name    :process.guard/no-child-entity-in-state?
   :type    :guard
   :handler (complement some-child-entity-in-state-handler)})

(def acceptance-in-state?
  {:name    :process.guard/acceptance-in-state?
   :summary (str "Special case of :process.guard/child-entity-in-state? that works with acceptance "
                 "entities, which may not be direct children of the target entity")
   :type    :guard
   :handler (fn [{[_ entity-k states] ::tk/guard
                  {:keys [target]}    :request
                  :as                 fsm}]
              (let [acceptance-id (guard-utils/get-param fsm entity-k)]
                (->> (concat (form-model/forms target)
                             (attachment-model/phase-attachments target))
                     (mapcat acceptance-model/acceptances-for-entity)
                     (filter (comp (partial = acceptance-id) :db/id))
                     first
                     :domain/state
                     (contains? states))))})

(def not-reversal-draft?
  {:name    :process.guard/not-reversal-draft?
   :summary "Checks that a bill is not a reversal draft (doesn't have a previous-bill link)."
   :type    :guard
   :handler (fn [fsm]
              (p/if-all-let [[_ entity-k] (-> fsm ::tk/guard)
                             bill (guard-utils/find-child-entity fsm entity-k)]
                (not (fee-model/reversal-bill? bill))))})

(def guards [child-entity-in-state?
             some-child-entity-in-state?
             no-child-entity-in-state?
             acceptance-in-state?
             not-reversal-draft?])
